import { createBrowserHistory } from 'history';
import { BLOCKED_FILTERS, FILTER_NAMES } from './consts';

export const navigate = (url, refresh = false) => {
  if (!url) {
    return;
  }

  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    // clean up the url before navigation
    let _url = '';
    const urlObj = new URL(`${window.location.origin}${url}`);
    const { pathname, searchParams } = urlObj;
    const search = searchParams.toString();
    _url = `${window.location.origin}${pathname}?${search}`;
    // Navigate to new URL
    const history = createBrowserHistory();
    history.push(_url);
    if (refresh) {
      history.go();
    }
  }
};

export const fetchJson = async (_path) => {
  try {
    const fetchJsonResponse = await fetch(_path);
    const gcMallJsonResponse = await fetchJsonResponse.json(fetchJsonResponse);
    return gcMallJsonResponse;
  } catch (error) {
    console.error(error); // eslint-disable-line
    return error;
  }
};

export const isLocalHost = () => {
  if (typeof window !== 'undefined') {
    const loc = window.location.pathname;
    const host = !!loc.match(/chapters/g);
    return host;
  }
  return false;
};

export const getDataReady = (data, defaultFilters) => {
  if (!data || !data.length) {
    return [];
  }
  if (!defaultFilters) {
    return data;
  }

  const _data = [...data];
  Object.keys(defaultFilters).forEach((filterName) => {
    _data.forEach((item) => {
      const _filters = defaultFilters[filterName];
      let availableFilters = [];
      _filters.forEach((fl) => {
        const _fl = fl.toLowerCase();
        if (item[_fl]) {
          availableFilters.push(fl);
        }
      });
      if (availableFilters.length > 0) {
        item[filterName] = availableFilters;
      }
    });
  });
  return _data;
};

export const mapFilterNameToKey = (filterName, allKeys) => {
  return allKeys.find((fullKey) => fullKey.includes(filterName.slice(0, -1).toLowerCase()));
};

export const getFilters = (filterNames, list) => {
  let allFilters = {};
  try {
    filterNames.forEach((filterName) => {
      let filters = new Set();
      list.forEach((item) => {
        const allKeys = Object.keys(item);
        const key = mapFilterNameToKey(filterName, allKeys);
        if (item[key] && item[key].constructor === Array && item[key].length > 0) {
          item[key].forEach(filters.add, filters);
        }
        if (item[key] && typeof item[key] === 'string') {
          filters.add(item[key]);
        }
      });
      // De-depulicate the filters and remove unwanted filters as requested
      let uniqueFilters = Array.from(filters)
        .sort((one, two) => {
          const first = one.toLowerCase();
          const second = two.toLowerCase();
          if (first < second) {
            return -1;
          }
          if (first > second) {
            return 1;
          }
          return 0;
        })
        .filter((fl) => !BLOCKED_FILTERS.includes(fl));
      if (uniqueFilters && uniqueFilters.length > 0) {
        // Always put own brand at the top of the filters
        const ownBrandIdx = uniqueFilters.findIndex((fl) => fl.toLowerCase().includes('home depot')
        );
        if (ownBrandIdx > -1) {
          const nameText = uniqueFilters[ownBrandIdx];
          uniqueFilters.splice(ownBrandIdx, 1);
          uniqueFilters.unshift(nameText);
        }
        allFilters[filterName.toLowerCase()] = uniqueFilters;
      }
    });
  } catch (err) {
    //
  }
  return allFilters;
};

export const getEnv = () => {
  let env = '';
  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    if (origin.match(/local/g)) {
      env = 'local';
    } else if (origin.match(/homedepotdev/g)) {
      env = 'llc';
    } else {
      env = 'prod';
    }
  }
  return env;
};

export const getJsonPath = () => {
  let firstPartyJsonUrl = '';
  let thirdPartyJsonUrl = '';
  let getBrandsJsonUrl = '';
  let currentEnv = getEnv();
  if (currentEnv === 'local' || currentEnv === 'llc') {
    firstPartyJsonUrl = 'https://assets-qa.thdstatic.com/giftcards-qa/catalog/listings/homedepot.json';
    thirdPartyJsonUrl = 'https://assets-qa.thdstatic.com/giftcards-qa/catalog/listings/thirdparty.json';
    getBrandsJsonUrl = 'https://assets-qa.thdstatic.com/giftcards-qa/catalog/listings/brand.json';
  } else if (currentEnv === 'prod') {
    firstPartyJsonUrl = 'https://assets.thdstatic.com/giftcards/catalog/listings/homedepot.json';
    thirdPartyJsonUrl = 'https://assets.thdstatic.com/giftcards/catalog/listings/thirdparty.json';
    getBrandsJsonUrl = 'https://assets.thdstatic.com/giftcards/catalog/listings/brand.json';
  }
  return { firstPartyJsonUrl, thirdPartyJsonUrl, getBrandsJsonUrl };
};

export const getFiltersFromURL = () => {
  let selectedFilters = {};
  if (typeof window !== 'undefined') {
    const params = new URL(window.location.href).searchParams;
    params.forEach((value, key) => {
      const filterName = key.replace(/_/g, ' ');
      if (filterName && FILTER_NAMES.findIndex((fn) => fn.toLowerCase() === filterName) > -1) {
        if (!selectedFilters[filterName]) {
          selectedFilters[filterName] = [];
        }
        const _val = value.replace(/_/g, ' ');
        const filterVal = _val.charAt(0).toUpperCase() + _val.slice(1);
        selectedFilters[filterName].push(filterVal);
      }
    });
  }
  return selectedFilters;
};

export const setFiltersToURL = (selectedFilters) => {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams();

    // Add filter params
    Object.keys(selectedFilters).forEach((filterName) => {
      const filterValues = selectedFilters[filterName];
      if (Array.isArray(filterValues) && filterValues.length > 0) {
        const paramKey = filterName.toLowerCase().replace(/ /g, '_');
        filterValues.forEach((value) => {
          let paramValue = '';
          if (value === 'The Home Depot') {
            paramValue = value.replace(/ /g, '_');
          } else {
            paramValue = value.toLowerCase().replace(/ /g, '_');
          }
          params.append(paramKey, paramValue);
        });
      }
    });

    // Preserve expversion if it exists
    const currentParams = new URLSearchParams(window.location.search);
    const expversion = currentParams.get('expversion');
    if (expversion) {
      params.set('debug', 'true');
      params.set('expversion', expversion);
    }

    const url = `/gift-cards/list?${params.toString()}`;

    if (!isLocalHost()) {
      navigate(url, true);
    } else {
      console.log('Navigate to : ', url); // eslint-disable-line
    }
  }
};

export const getNextGiftCardURL = (cardName, productLineId, configurationId) => {
  const url = `/gift-cards/p/${cardName?.split(' ').join('-')}/${productLineId}/${configurationId}`;
  return url;
};

export const navigateToGiftCardPIPPage = (giftCardData) => {
  const { name, productLineId, digital, physical } = giftCardData;
  const configurationId = digital?.configurationId || physical?.configurationId;
  let url = getNextGiftCardURL(name, productLineId, configurationId);

  // if we have expversion in URL, then constructing & passing the same to PIP
  const params = new URLSearchParams(window.location.search);
  const preRelease = params.get('expversion');
  if (preRelease !== null) {
    const preReleaseUrl = `?debug=true&expversion=${preRelease}`;
    url += preReleaseUrl;
  }

  if (!isLocalHost()) {
    navigate(url, true);
  } else {
    console.log('Navigate to : ', url); // eslint-disable-line
  }
};